import {
  CaseCreation,
  CaseDefendantLink,
  CommentOnCaseDraft,
  CoordinatorCommentsOnDailyReport,
  CountDefendantsList,
  DailyReport,
  DefendantCaseDetail,
  DefendantCaseDetailDirectorComment,
  Exhibit,
  FinalVerdict,
  OpeniningCaseFile,
  PreJudgement,
  Reporter,
  Suspect,
  SuspectTowitness,
  Victim,
  Witness,
  WitnessDefendants,
} from "types";
import apiClient from "../lib/apiClients";
import { CourtMarks, withCourtMarks } from "components/CourtFilingMarks";

// CaseCreation — page-number paginated ({ count, next, previous, results }).
export interface CaseListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  status_filter?: string;
}

const buildCaseListQuery = (
  params?: CaseListParams,
  extra?: Record<string, string | number>,
) => {
  const q = new URLSearchParams();
  if (extra)
    Object.entries(extra).forEach(([k, v]) => q.set(k, String(v ?? "")));
  if (params?.page) q.set("page", String(params.page));
  if (params?.pageSize) q.set("page_size", String(params.pageSize));
  if (params?.search) q.set("search", params.search);
  if (params?.status) q.set("status", params.status);
  if (params?.status_filter) q.set("status_filter", params.status_filter);
  return q.toString();
};

const toCasePage = (data: any): PageResult<CaseCreation> =>
  Array.isArray(data)
    ? { count: data.length, next: null, previous: null, results: data }
    : {
        count: data?.count ?? data?.results?.length ?? 0,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        results: data?.results ?? [],
      };

export const fetchCaseCreation = async (
  params?: CaseListParams,
): Promise<PageResult<CaseCreation>> => {
  const qs = buildCaseListQuery(params);
  const response = await apiClient.get(
    `/crime_case_management/case-creations/${qs ? `?${qs}` : ""}`,
  );
  return toCasePage(response.data);
};
export const fetchCaseCreationByCoordinatorWithType = async (
  admin_unit: string | number,
  org_id: string | number,
  role: string,
  params?: CaseListParams,
): Promise<PageResult<CaseCreation>> => {
  const qs = buildCaseListQuery(params, {
    coordinator_role: role,
    admin_unit_id: admin_unit,
  });
  const response = await apiClient.get(
    `/crime_case_management/case-creations/by-coordinator-role/?${qs}`,
  );
  return toCasePage(response.data);
};
export const fetchCaseCreationByRegistrarWithType = async (
  admin_unit: string | number,
  role: string,
  params?: CaseListParams,
): Promise<PageResult<CaseCreation>> => {
  const qs = buildCaseListQuery(params, {
    admin_unit_id: admin_unit,
    registrar_role: role,
  });
  const response = await apiClient.get(
    `/crime_case_management/case-creations/by-registrar-role/?${qs}`,
  );
  return toCasePage(response.data);
};
export const fetchCaseCreationByProsecutorId = async (
  prosecutor_id: string | number,
  params?: CaseListParams,
): Promise<PageResult<CaseCreation>> => {
  const qs = buildCaseListQuery(params, {
    assigned_prosecutor_userid: prosecutor_id,
  });
  const response = await apiClient.get(
    `/crime_case_management/case-creations/by-assigned-prosecutor/?${qs}`,
  );
  return toCasePage(response.data);
};

// --- Upcoming court hearings / appointments (future-dated, soonest first) ---
export type HearingType = "charge" | "appeal" | "bail";

export interface UpcomingHearing {
  id: string;
  case_id: string;
  category: string; // backend hearing category (charge / appeal / bail / ...)
  source: string; // which record the hearing originates from
  registration_number: string;
  crime_type: string;
  case_status: string;
  admin_unit_id: string;
  assigned_prosecutor_name: string;
  appointment_date: string;
  appointment_type: string;
  appointment_reason: string;
  court_order_for_next_appointment: string;
  court_registration_number: string;
  court_type: string;
  court_level: string;
  report_date: string;
  status: string;
}

export interface UpcomingHearingsParams {
  admin_unit_id?: string | number;
  assigned_prosecutor_userid?: string | number;
  coordinator_role?: string; // role whose crime types to filter by (with admin_unit_id)
  hearing_type?: HearingType; // charge | appeal | bail (omit for All)
  from?: string; // YYYY-MM-DD lower bound on the hearing date
  to?: string; // YYYY-MM-DD upper bound on the hearing date
  days?: number; // window from today (e.g. 7 -> next week)
  page?: number;
  pageSize?: number;
}

export interface PageResult<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const fetchUpcomingHearings = async (
  params?: UpcomingHearingsParams,
): Promise<PageResult<UpcomingHearing>> => {
  const q = new URLSearchParams();
  if (params?.admin_unit_id)
    q.set("admin_unit_id", String(params.admin_unit_id));
  if (params?.assigned_prosecutor_userid)
    q.set(
      "assigned_prosecutor_userid",
      String(params.assigned_prosecutor_userid),
    );
  if (params?.coordinator_role)
    q.set("coordinator_role", params.coordinator_role);
  if (params?.hearing_type) q.set("hearing_type", params.hearing_type);
  if (params?.from) q.set("from", params.from);
  if (params?.to) q.set("to", params.to);
  if (params?.days) q.set("days", String(params.days));
  if (params?.page) q.set("page", String(params.page));
  if (params?.pageSize) q.set("page_size", String(params.pageSize));
  const qs = q.toString();

  const response = await apiClient.get(
    `/crime_case_management/case-creations/upcoming-hearings/${
      qs ? `?${qs}` : ""
    }`,
  );
  const data: any = response.data;
  return Array.isArray(data)
    ? { count: data.length, next: null, previous: null, results: data }
    : {
        count: data?.count ?? data?.results?.length ?? 0,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        results: data?.results ?? [],
      };
};
export const fetchCaseCreationByCRNumberAndIncidentRegNo = async (
  crime_reference_number: string,
  incident_registration_no: string,
): Promise<CaseCreation[]> => {
  const response = await apiClient.get(
    `/crime_case_management/case-creations/?incident_registration_no=${incident_registration_no}&crime_refernce_number=${crime_reference_number}`,
  );
  // The base list endpoint is now paginated; unwrap to keep array callers working.
  return toCasePage(response.data).results;
};
export const fetchCaseCreationById = async (id: number | string) => {
  const response = await apiClient.get<CaseCreation>(
    "/crime_case_management/case-creations/" + id + "/",
  );
  return response.data;
};
export const fetchCaseCreationDefendants = async (id: number | string) => {
  const response = await apiClient.get<DefendantCaseDetail[]>(
    "/crime_case_management/case-creations/" + id + "/defendants/",
  );
  return response.data;
};
export const fetchCaseCreationRelatedParties = async (id: number | string) => {
  const response = await apiClient.get<{
    suspects: Suspect[];
    witnesses: Witness[];
    victims: Victim[];
    reporters: Reporter[];
    exhibits: Exhibit[];
  }>("/crime_case_management/case-creations/" + id + "/related-parties/");
  return response.data;
};
export const addCaseCreation = async (data: CaseCreation) => {
  const response = await apiClient.post(
    "/crime_case_management/case-creations/",
    data,
  );
  return response.data;
};
export const editCaseCreation = async (data: any) => {
  const response = await apiClient.patch(
    "/crime_case_management/case-creations/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const editCaseCreationWithupload = async (data: any) => {
  const response = await apiClient.patch(
    "/crime_case_management/case-creations/" + data?.id + "/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const deleteCaseCreation = async (reporter: CaseCreation) => {
  const response = await apiClient.delete(
    "/crime_case_management/case-creations/" + reporter?.id + "/",
  );
  return response.data;
};

// CaseDefendantLink
export const fetchCaseDefendantLink = async () => {
  const response = await apiClient.get<CaseDefendantLink[]>(
    "/crime_case_management/case-defendant-links/",
  );
  return response.data;
};
export const addCaseDefendantLink = async (data: any) => {
  const response = await apiClient.post(
    "/crime_case_management/case-defendant-links/",
    data,
  );
  return response.data;
};
export const editCaseDefendantLink = async (data: any) => {
  const response = await apiClient.patch(
    "/crime_case_management/case-defendant-links/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const deleteCaseDefendantLink = async (id: any) => {
  const response = await apiClient.delete(
    "/crime_case_management/case-defendant-links/" + id + "/",
  );
  return response.data;
};

// WitnessDefendants
export const fetchWitnessDefendants = async () => {
  const response = await apiClient.get<WitnessDefendants[]>(
    "/crime_case_management/witness-defendants/",
  );
  return response.data;
};
export const addWitnessDefendants = async (data: any) => {
  const response = await apiClient.post(
    "/crime_case_management/witness-defendants/",
    data,
  );
  return response.data;
};
export const editWitnessDefendants = async (data: any) => {
  const response = await apiClient.patch(
    "/crime_case_management/witness-defendants/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const deleteWitnessDefendants = async (id: any) => {
  const response = await apiClient.delete(
    "/crime_case_management/witness-defendants/" + id + "/",
  );
  return response.data;
};

// CaseDefendantList
export const fetchCountDefendantList = async () => {
  const response = await apiClient.get<CountDefendantsList[]>(
    "/crime_case_management/count-defendants/",
  );
  return response.data;
};
export const addCountDefendantList = async (data: any) => {
  const response = await apiClient.post(
    "/crime_case_management/count-defendants/",
    data,
  );
  return response.data;
};
export const editCountDefendantList = async (data: any) => {
  const response = await apiClient.patch(
    "/crime_case_management/count-defendants/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const deleteCountDefendantList = async (id: any) => {
  const response = await apiClient.delete(
    "/crime_case_management/count-defendants/" + id + "/",
  );
  return response.data;
};

// PreJudgement
export const fetchPreJudgement = async () => {
  const response = await apiClient.get<PreJudgement[]>(
    "/crime_case_management/pre-judgement-entries/",
  );
  return response.data;
};
export const addPreJudgement = async (data: any) => {
  const response = await apiClient.post(
    "/crime_case_management/pre-judgement-entries/",
    data,
  );
  return response.data;
};

// DefendantCase
export const fetchDefendantCase = async () => {
  const response = await apiClient.get<DefendantCaseDetail[]>(
    "/crime_case_management/defendant-case-details/",
  );
  return response.data;
};
export const addDefendantCase = async (data: DefendantCaseDetail) => {
  const formData = new FormData();

  // Append simple fields
  Object.entries(data).forEach(([key, value]) => {
    if (
      key !== "defendants" &&
      key !== "witness" &&
      key !== "upload_charge_draft_upload"
    ) {
      if (value !== undefined && value !== null) {
        formData.append(key, value as any);
      }
    }
  });

  // Append arrays
  if (Array.isArray(data.defendants)) {
    data.defendants.forEach((id) => formData.append("defendants", String(id)));
  }
  if (Array.isArray(data.witness)) {
    data.witness.forEach((id) => formData.append("witness", String(id)));
  }

  // Append file
  if (data.upload_charge_draft_upload) {
    formData.append(
      "upload_charge_draft_upload",
      data.upload_charge_draft_upload,
    );
  }

  const response = await apiClient.post(
    "/crime_case_management/defendant-case-details/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editDefendantCase = async (data: any) => {
  const response = await apiClient.patch(
    "/crime_case_management/defendant-case-details/" + data?.id + "/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const deleteDefendantCase = async (reporter: DefendantCaseDetail) => {
  const response = await apiClient.delete(
    "/crime_case_management/defendant-case-details/" + reporter?.id + "/",
  );
  return response.data;
};

// Coordinator Comment Defendant Case Detail List
export const fetchDefendantCaseCoordinatorComment = async () => {
  const response = await apiClient.get<CommentOnCaseDraft[]>(
    "/crime_case_management/defendant-case-coordinator-comments/",
  );
  return response.data;
};
export const fetchDailyReportByDefendantCaseDetailId = async (id: number) => {
  const response = await apiClient.get<DailyReport[]>(
    "/crime_case_management/defendant-case-details/" + id + "/daily_reports/",
  );
  return response.data;
};
export const fetchFinalVerdictByDefendantCaseDetailId = async (id: number) => {
  const response = await apiClient.get<FinalVerdict[]>(
    "/crime_case_management/defendant-case-details/" + id + "/final_verdicts/",
  );
  return response.data;
};
export const addCommentOnCaseDraft = async (data: CommentOnCaseDraft) => {
  const response = await apiClient.post(
    "/crime_case_management/comment-on-case-drafts/",
    data,
  );
  return response.data;
};
// Director Comment Defendant Case Detail List
export const fetchDefendantCaseDirectorComment = async () => {
  const response = await apiClient.get<DefendantCaseDetailDirectorComment[]>(
    "/crime_case_management/defendant-case-director-comments/",
  );
  return response.data;
};
export const addDefendantCaseDirectorComment = async (
  data: DefendantCaseDetailDirectorComment,
) => {
  const response = await apiClient.post(
    "/crime_case_management/defendant-case-director-comments/",
    data,
  );
  return response.data;
};

// CourtDetail
export const fetchOpeningCaseFile = async () => {
  const response = await apiClient.get<OpeniningCaseFile[]>(
    "/crime_case_management/opening-case-files/",
  );
  return response.data;
};
export const fetchOpeningCaseFileById = async (id: number) => {
  const response = await apiClient.get<OpeniningCaseFile>(
    "/crime_case_management/openingcasefiles/" + id + "/",
  );
  return response.data;
};
export const fetchOpeningCaseFileByDefendantCaseDetailId = async (
  id: number,
) => {
  const response = await apiClient.get<OpeniningCaseFile[]>(
    "/crime_case_management/openingcasefiles/by-defendant/" + id + "/",
  );
  return response.data;
};
export const addOpeningCaseFile = async (data: OpeniningCaseFile) => {
  const response = await apiClient.post(
    "/crime_case_management/opening-case-files/",
    data,
  );
  return response.data;
};
export const editOpeningCaseFile = async (data: any) => {
  const response = await apiClient.patch(
    "/crime_case_management/opening-case-files/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const deleteOpeningCaseFile = async (reporter: OpeniningCaseFile) => {
  const response = await apiClient.delete(
    "/crime_case_management/opening-case-files/" + reporter?.id + "/",
  );
  return response.data;
};

// DailyReport
export const fetchDailyReport = async () => {
  const response = await apiClient.get<DailyReport[]>(
    "/crime_case_management/daily-reports/",
  );
  return response.data;
};
export const fetchDailyReportByDefendantCaseDetail = async (
  id: number | string | undefined,
) => {
  const response = await apiClient.get<DailyReport[]>(
    `/crime_case_management/defendant-case-details/${id}/daily_reports/`,
  );
  return response.data;
};
export const fetchDailyReportById = async (id: number) => {
  const response = await apiClient.get<DailyReport>(
    "/crime_case_management/daily-reports/" + id + "/",
  );
  return response.data;
};
export const addDailyReport = async (data: DailyReport) => {
  const response = await apiClient.post(
    "/crime_case_management/daily-reports/",
    data,
  );
  return response.data;
};
export const editDailyReport = async (data: any) => {
  const response = await apiClient.patch(
    "/crime_case_management/daily-reports/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const deleteDailyReport = async (reporter: DailyReport) => {
  const response = await apiClient.delete(
    "/crime_case_management/daily-reports/" + reporter?.id + "/",
  );
  return response.data;
};

// FinalVerdict
export const fetchFinalVerdict = async () => {
  const response = await apiClient.get<FinalVerdict[]>(
    "/crime_case_management/final-verdicts/",
  );
  return response.data;
};
export const fetchFinalVerdictByDefendantCaseDetail = async (
  id: number | string | undefined,
) => {
  const response = await apiClient.get<FinalVerdict[]>(
    `/crime_case_management/defendant-case-details/${id}/final_verdicts/`,
  );
  return response.data;
};
export const fetchFinalVerdictById = async (id: number) => {
  const response = await apiClient.get<FinalVerdict>(
    "/crime_case_management/final-verdicts/" + id + "/",
  );
  return response.data;
};
export const addFinalVerdict = async (data: any) => {
  const response = await apiClient.post(
    "/crime_case_management/final-verdicts/",
    data,
  );
  return response.data;
};
export const editFinalVerdict = async (data: any) => {
  const response = await apiClient.patch(
    "/crime_case_management/final-verdicts/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const deleteFinalVerdict = async (reporter: FinalVerdict) => {
  const response = await apiClient.delete(
    "/crime_case_management/final-verdicts/" + reporter?.id + "/",
  );
  return response.data;
};

// Coordinator Comment Daily Report List
export const fetchCoordinatorCommentsOnDailyReport = async () => {
  const response = await apiClient.get<CoordinatorCommentsOnDailyReport[]>(
    "/crime_case_management/coordinator-comment-daily-reports/",
  );
  return response.data;
};
export const fetchCoordinatorCommentsOnDailyReportByDailyReportId = async (
  id: string | number,
) => {
  const response = await apiClient.get<{
    coordinator_comments: CoordinatorCommentsOnDailyReport[];
  }>("/crime_case_management/daily-reports/" + id + "/all-comments/");
  return response.data;
};
export const addCoordinatorCommentsOnDailyReport = async (
  data: CoordinatorCommentsOnDailyReport,
) => {
  const response = await apiClient.post(
    "/crime_case_management/coordinator-comment-daily-reports/",
    data,
  );
  return response.data;
};

// SuspectToWitness (SuspectToWitness)
export const fetchSuspectToWitness = async () => {
  const response = await apiClient.get<SuspectTowitness[]>(
    "/crime_case_management/suspect-to-witness-conversions/",
  );
  return response.data;
};
export const fetchSuspectToWitnessByCRNumberAndIncidentRegNo = async (
  crime_reference_number: string,
  incident_registration_no: string,
) => {
  const response = await apiClient.get<SuspectTowitness[]>(
    `/crime_case_management/suspect-to-witness-conversions/?crime_registration_number=${crime_reference_number}&incident_registration_number=${incident_registration_no}`,
  );
  return response.data;
};
export const fetchSuspectToWitnessById = async (id: any) => {
  const response = await apiClient.get<SuspectTowitness>(
    "/crime_case_management/suspect-to-witness-conversions/" + id + "/",
  );
  return response.data;
};
export const addSuspectToWitness = async (data: SuspectTowitness) => {
  const response = await apiClient.post(
    "/crime_case_management/suspect-to-witness-conversions/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editSuspectToWitness = async (data: any) => {
  const response = await apiClient.patch(
    "/crime_case_management/suspect-to-witness-conversions/" + data?.id + "/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editSuspectToWitness2 = async (data: any, marks?: CourtMarks) => {
  const { body, config } = withCourtMarks(data, marks);
  const response = await apiClient.patch(
    "/crime_case_management/suspect-to-witness-conversions/" + data?.id + "/",
    body,
    config,
  );
  return response.data;
};
export const deleteSuspectToWitness = async (id: number) => {
  const response = await apiClient.delete(
    "/crime_case_management/suspect-to-witness-conversions/" + id + "/",
  );
  return response.data;
};
export const addSuspectToWitnessFeedback = async (data: any) => {
  const response = await apiClient.post(
    "/crime_case_management/respond-back-comments/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

// CASE RESPOND BACK
export const addCaseRespondBackComment = async (data: any) => {
  const response = await apiClient.post(
    "/crime_case_management/respinsbac_comment-on-case-drafts/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

// CHARGE DRAFT HISTORY
export const addChargeDraftHistory = async (data: any) => {
  const response = await apiClient.post(
    "/crime_case_management/charge-draft-history/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
// CHARGE DRAFT HISTORY
export const addChangeOfVuenue = async (data: any) => {
  const response = await apiClient.post(
    "/crime_case_management/change-of-venues/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editChangeOfVenue = async (data: any) => {
  const response = await apiClient.patch(
    "/crime_case_management/change-of-venues/" + data?.id + "/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const deleteChangeOfVenue = async (id: number) => {
  const response = await apiClient.delete(
    "/crime_case_management/change-of-venues/" + id + "/",
  );
  return response.data;
};
export const fetchChangeOfVenueByCRNumberAndIncidentRegNo = async (
  crime_reference_number: string,
  incident_registration_number: string,
) => {
  const response = await apiClient.get(
    `/crime_case_management/change-of-venues/filter-by-registration-number/?crime_registration_number=${crime_reference_number}&incident_registration_number=${incident_registration_number}`,
  );
  return response.data;
};
export const fetchChangeOfVenueById = async (id: string | number) => {
  const response = await apiClient.get(
    `/crime_case_management/change-of-venues/${id}/`,
  );
  return response.data;
};
// Venue Feedback
export const addChangeOfVuenueFeedback = async (data: any) => {
  const response = await apiClient.post(
    "/crime_case_management/venue-feedbacks/",
    data,
  );
  return response.data;
};

// Case Relation
export const addCaseRelation = async (data: any) => {
  const response = await apiClient.post(
    "/crime_case_management/case-relations/",
    data,
  );
  return response.data;
};
export const fetchCaseRelation = async () => {
  const response = await apiClient.get(
    `/crime_case_management/case-relations/`,
  );
  return response.data;
};
